import React, { createContext, useCallback, useEffect, useState } from 'react';

import { usePrevious } from '../../hooks/usePrevious';
import { Guid } from '../../store/model/guid';
import { getCurrentItem } from '../../store/selectors/getCurrentItem';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { useLinearModeDataSelector } from '../RestrictMenu/LinearMode/useLinearModeDataSelector';
import { useDisplayProgressDialog } from './useDisplayProgressDialog';
import { useUnlockedItemsObserver } from './useUnlockedItemsObserver';
import { getLastItemInArray } from '@ael/core-api';
import { isEqual } from 'lodash';

type GoTo = {
  itemId: Guid;
};

type LinearProgressProvided = {
  displayGoTo: GoTo | null;
};

type LinearProgressContextProviderProps = {
  isLinearModeOn: boolean;
  children?: React.ReactNode;
};

export const LinearProgressContext = createContext<LinearProgressProvided>({ displayGoTo: null });

export const LinearProgressContextProvider = (props: LinearProgressContextProviderProps) => {
  const displayProgressDialog = useDisplayProgressDialog();
  const currentItemId = useStudentSelector(getCurrentItem)?.id;
  const [goToItemId, setGoToItemId] = useState<Guid | null>(null);
  const linearModeData = useLinearModeDataSelector(props.isLinearModeOn);
  const prevLinearModeData = usePrevious(linearModeData);
  useEffect(() => {
    if (linearModeData.areAllUnlocked) {
      return;
    }

    if (isEqual(prevLinearModeData?.getAllUnlocked(), linearModeData.getAllUnlocked())) {
      return;
    }

    setGoToItemId(getLastItemInArray(linearModeData.getAllUnlocked()));
  }, [linearModeData, prevLinearModeData]);

  const onUnlocked = useCallback((newlyUnlockedItemIds: Guid[]) => {
    setGoToItemId(getLastItemInArray(newlyUnlockedItemIds));
  }, []);
  const onReset = useCallback((unlockedItemIds: Guid[]) => {
    setGoToItemId(getLastItemInArray(unlockedItemIds));
  }, []);
  useUnlockedItemsObserver({ onUnlocked, onReset, isLinearModeOn: props.isLinearModeOn });

  useEffect(() => {
    if (currentItemId) {
      setGoToItemId(null);
    }
  }, [currentItemId]);

  const displayGoTo = displayProgressDialog && goToItemId ? { itemId: goToItemId } : null;
  return <LinearProgressContext.Provider value={{ displayGoTo }}>{props.children}</LinearProgressContext.Provider>;
};
